import { useAuth0 } from '@auth0/auth0-react';
import { LDProvider, useLDClient, type LDContext, type LDOptions } from 'launchdarkly-react-client-sdk';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { FEATURE_FLAGS } from './feature-flags.constants';

type FeatureFlagContextType = {
  isEnabled: (flagKey: string, defaultValue?: boolean) => boolean;
  isReady: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

function FeatureFlagWrapper({ children, isFallback = false }: { children: ReactNode; isFallback?: boolean }) {
  const ldClient = useLDClient();
  const isReady = isFallback || !!ldClient;
  const isEnabled = useCallback(
    (flagKey: string, defaultValue = false) => {
      try {
        const result = ldClient?.variation(flagKey, defaultValue);
        return result ?? defaultValue;
      } catch (error) {
        console.warn(`Feature flag error for ${flagKey}:`, error);
        return defaultValue;
      }
    },
    [ldClient],
  );

  const contextValue: FeatureFlagContextType = useMemo(
    () => ({
      isEnabled,
      isReady,
    }),
    [isEnabled, isReady],
  );

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}

const LD_OPTIONS: LDOptions = {
  streaming: false,
  bootstrap: 'localStorage',
};

const StableLDProvider = ({
  clientSideID,
  context,
  children,
}: {
  clientSideID: string;
  context: LDContext;
  children: ReactNode;
}) => {
  // LDProvider reads context/options only on mount, so later identity changes
  // are handled by ContextUpdater. Render `children` directly: wrapping them in
  // a component built once froze the first render's children (stuck skeleton).
  const [initialContext] = useState(context);

  return (
    <LDProvider clientSideID={clientSideID} timeout={5} context={initialContext} options={LD_OPTIONS}>
      <FeatureFlagWrapper>{children}</FeatureFlagWrapper>
    </LDProvider>
  );
};

function ContextUpdater({ context }: { context?: LDContext }) {
  const ldClient = useLDClient();

  useEffect(() => {
    if (ldClient && context) {
      // Update the LaunchDarkly context without recreating the provider
      ldClient.identify(context).catch((error) => {
        console.warn('Failed to update LaunchDarkly context:', error);
      });
    }
  }, [ldClient, context]);

  return null;
}

/**
 * Ensures LaunchDarkly records evaluations for flags that may not be read by
 * a visible component on initial load (e.g. admin dashboard, chat feedback v2).
 * Call variation() once when the client is ready so the dashboard shows fetches.
 */
function EnsureFlagEvaluations() {
  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) return;
    ldClient.variation(FEATURE_FLAGS.CHAT_FEEDBACK_V2_ENABLED, false);
  }, [ldClient]);

  return null;
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth0();
  const effectiveClientId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  const context = useMemo(() => {
    if (!isAuthenticated || !user || !user.sub || !user.org_id) {
      return undefined;
    }
    return {
      kind: 'multi',
      user: { key: user.sub },
      organization: { key: user.org_id },
    };
  }, [isAuthenticated, user?.sub, user?.org_id]);

  // Keep using the last valid context across transient auth-state nulls
  // (e.g. Auth0 silent renewal) so flag evaluations stay stable mid-session.
  // On a real logout (`isAuthenticated` flips false) drop the cached identity
  // so the provider falls back instead of staying bound to the signed-out user.
  const lastContextRef = useRef<LDContext | undefined>(undefined);
  if (context) {
    lastContextRef.current = context;
  } else if (!isAuthenticated) {
    lastContextRef.current = undefined;
  }
  const effectiveContext = context ?? lastContextRef.current;

  if (!effectiveClientId) {
    console.warn('FeatureFlagProvider: No LaunchDarkly client ID found, using fallback mode');
    return <FeatureFlagWrapper isFallback>{children}</FeatureFlagWrapper>;
  }

  if (!effectiveContext) {
    return <FeatureFlagWrapper isFallback>{children}</FeatureFlagWrapper>;
  }

  return (
    <StableLDProvider clientSideID={effectiveClientId} context={effectiveContext}>
      <ContextUpdater context={effectiveContext} />
      <EnsureFlagEvaluations />
      {children}
    </StableLDProvider>
  );
}

export function useFeatureFlagContext(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }

  return context;
}
